/**
 * The SYMBOL shown to a person. Storage stays `MYR` (CLAUDE.md §7.3) — this is
 * presentation, and the design reference §7 is explicit: `RM 1,234.56`. RM is
 * what a customer in Kuala Lumpur reads on a receipt; MYR is what a ledger
 * stores.
 */
const SYMBOL: Record<string, string> = { MYR: 'RM' };

/**
 * Formats a decimal STRING for display without ever making it a number.
 *
 * `Number('89900.10')` is a float, and a float is what the API deliberately
 * avoided all the way down to the database. Formatting is a string operation:
 * split on the point, group the integer part, pad the fraction.
 */
export function formatMoney(value: string, currency = 'MYR'): string {
  const negative = value.startsWith('-');
  const [wholeRaw = '0', fractionRaw = ''] = value.replace('-', '').split('.');

  const grouped = wholeRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const fraction = `${fractionRaw}00`.slice(0, 2);

  // Sign sits with the amount, not the currency: it is the value that is
  // negative. "RM -500.00", never "-RM 500.00".
  const symbol = SYMBOL[currency] ?? currency;
  return `${symbol} ${negative ? '-' : ''}${grouped}.${fraction}`;
}

/** True when a decimal string is greater than zero, without parsing it. */
export function isPositive(value: string): boolean {
  return !value.startsWith('-') && /[1-9]/.test(value);
}

/** A plain decimal string in sen, for comparing without a float. "12.3" gives 1230n. */
function toSen(value: string): bigint {
  const negative = value.startsWith('-');
  const [whole = '0', fraction = ''] = value.replace('-', '').split('.');
  const sen = BigInt(whole === '' ? '0' : whole) * 100n + BigInt(`${fraction}00`.slice(0, 2));
  return negative ? -sen : sen;
}

/** True when decimal string `a` is more than `b`, to the sen. */
export function isMoreThan(a: string, b: string): boolean {
  return toSen(a) > toSen(b);
}
